import React, { useEffect, useRef, useState } from 'react';
import { Card, Typography, Button, message, Modal, InputNumber, notification } from 'antd';
import { DollarOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getResellerData } from '@/store/reseller/selector';
import {
	createCheckoutSessionNewResellers,
	cancelResellerSubscription,
	updateTutorialStepStatus,
	updateTourStatus,
} from '@/api/reseller';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ResellerDashboard from '../ResellerDashboard';
import { ResellerSiderItem } from 'src/enums/IResellerSiderItems';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { updateReseller } from '@/store/reseller/slice';
import CouponRedemption from '@/components/SubscriptionComponents/CouponRedemption';
import RenderCouponInfo from '@/components/SubscriptionComponents/RenderCouponInfo/RenderCouponInfo';
import CancelSubReasonModal from './Modals/CancelSubReasonModal';
import { MODAL_IDS } from 'src/enums/EModal';
import { closeModal, openModal } from '@/store/modal/slice';
import { BookOpenIcon, SettingsIcon } from 'lucide-react';
import TutorialsModal from '../TutorialsModal';
import ResellerTour from '../ResellerTour/ResellerTour';
import TourButton from '@/components/TourButton/TourButton';
import { setTour } from '@/store/resellerTour/slice';
import { AppRoutes } from 'src/enums/ERoutes';
import { getResellerTourState } from '@/store/resellerTour/selector';
import {
	getSubscriptionInformation,
	getCoupons,
	applyCouponCode,
	removeDiscount,
	createClientPortalSession,
} from '@/api/user';
import { getModalData } from '@/store/modal/selector';

const { Title, Text } = Typography;

const tutorialItems = [
	{
		icon: (
			<div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full">
				<SettingsIcon className="text-blue-500" size={20} />
			</div>
		),
		title: 'Subscription and Credits',
		description: 'Guide on managing AI Frontdesk subscription and credits',
		url: 'https://www.myaifrontdesk.com/reseller-tutorials/managing-ai-frontdesk-subscription-and-credits',
	},
];

const ResellerSubscription: React.FC = () => {
	const dispatch = useAppDispatch();

	const resellerData = useAppSelector(getResellerData);
	const [purchaseSeatsModal, setPurchaseSeatsModal] = useState<boolean>(false);
	const [seatsToBuy, setSeatsToBuy] = useState<number | null>(5);

	const queryClient = useQueryClient();
	const [isTutorialsModalOpen, setIsTutorialsModalOpen] = useState(false);

	const resellerSubscriptionTourRef1 = useRef<HTMLDivElement>(null);
	const resellerSubscriptionTourRef2 = useRef<HTMLDivElement>(null);

	const subscriptionTourSteps = [
		{
			title: <h1 className="font-bold text-2xl">Manage your subscription</h1>,
			description: (
				<p className="text-base">
					This is where you can view your subscription details. Need help? Click the Tutorials tab
					on the right for a detailed walkthrough.
				</p>
			),
			target: resellerSubscriptionTourRef1.current as HTMLElement,
		},
		{
			title: <h1 className="font-bold text-2xl">Manage Payment Method & Monitor Client Usage</h1>,
			description: (
				<p className="text-base">
					Update your payment details here and track your clients' minute usage—all in one place.
				</p>
			),
			target: resellerSubscriptionTourRef2.current as HTMLElement,
		},
	];

	const { data: billingOverview, isPending: isLoadingBillingOverview } = useQuery({
		queryKey: ['billingOverview', resellerData?.emailAddress],
		queryFn: () => getSubscriptionInformation('Reseller'),
		enabled: !!resellerData?.emailAddress,
		refetchOnWindowFocus: false,
	});

	const { data: couponData, refetch: refreshCoupons } = useQuery({
		queryKey: ['coupons', resellerData?.emailAddress],
		queryFn: () => getCoupons('Reseller'),
		enabled: !!resellerData?.emailAddress,
	});

	const updateTutorialMutation = useMutation({
		mutationFn: updateTutorialStepStatus,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['reseller'] });
		},
	});

	const updateToursMutation = useMutation({
		mutationFn: updateTourStatus,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['reseller'] });
		},
	});

	const resellerTour = useAppSelector(getResellerTourState);

	useEffect(() => {
		if (!resellerData?.emailAddress || resellerTour.open) {
			return;
		}

		if (!resellerData?.tours?.subscription) {
			updateToursMutation.mutate('subscription');
			dispatch(
				setTour({
					open: true,
					currentStep: 0,
					returnTo: AppRoutes.RESELLER_SUBSCRIPTION,
				}),
			);
		}
	}, [resellerData?.emailAddress, resellerTour.open]);

	const stripeUpdatePaymentMethodMutation = useMutation({
		mutationFn: createClientPortalSession,
		onSuccess: (data) => {
			if (data.status === 'success') {
				window.location.href = data.redirectPaymentURL as string;
			} else {
				message.error('Failed to update payment method');
			}
		},
		onError: () => {
			message.error('An error occurred while updating the payment method');
		},
	});

	const createCheckoutSessionNewResellersMutation = useMutation({
		mutationFn: createCheckoutSessionNewResellers,
		onSuccess: (data: any) => {
			setPurchaseSeatsModal(false);
			if (data?.redirectPaymentURL) {
				message.success('Redirecting to payment page');

				window.location.href = data?.redirectPaymentURL;

				window.gtag('event', 'resellerPurchaseClicked', {
					event_category: 'EditDemo.js',
					event_label: 'success',
				});
			} else if (data.quantity) {
				message.success(`Seats Added to your subscription ${data.quantity}`);
				dispatch(
					updateReseller({
						...resellerData,
						totalInboundAgents: resellerData?.totalInboundAgents + data.quantity,
						unConfiguredInboundAgents: resellerData?.unConfiguredInboundAgents + data.quantity,
					}),
				);
			}
		},
		onError: () => {
			message.error('Error creating checkout session');
		},
	});

	const handleTutorialClick = () => {
		if (!resellerData?.tutorial_steps?.subscription_tutorial) {
			updateTutorialMutation.mutate('subscription_tutorial');
		}
		setIsTutorialsModalOpen(true);
	};

	const handleTourClick = () => {
		setIsTutorialsModalOpen(false);

		if (!resellerData?.tours?.subscription) {
			updateToursMutation.mutate('subscription');
		}
	};

	const handleUpdatePaymentMethodConfirm = () => {
		stripeUpdatePaymentMethodMutation.mutate({
			action: 'payment_method_update',
			clientType: 'Reseller',
		});
	};

	const handlePurchaseSeats = () => {
		if (seatsToBuy) {
			createCheckoutSessionNewResellersMutation.mutate({
				resellerEmail: resellerData?.emailAddress as string,
				quantity: seatsToBuy,
			});
		}
	};

	const cancelResellerSubscriptionMutation = useMutation({
		mutationFn: cancelResellerSubscription,
		onSuccess: (data: any, { subscriptionType }) => {
			message.success('Subscription would be canceled on ' + data?.date);
			dispatch(
				updateReseller({
					...resellerData,
					[subscriptionType === 'Inbound'
						? 'inboundScheduleSubCancelOn'
						: 'outboundScheduleSubCancelOn']: data?.date,
				}),
			);
			dispatch(closeModal());
		},
		onError: () => {
			message.error('Failed to cancel subscription');
		},
	});

	const handleCancelSubscription = (type: 'Inbound' | 'Outbound') => {
		const subscriptionId =
			type === 'Inbound'
				? resellerData?.inboundSubscriptionId
				: resellerData?.outboundSubscriptionId;
		const subscriptionStatus =
			type === 'Inbound'
				? resellerData?.inboundSubscriptionStatus
				: resellerData?.outboundSubscriptionStatus;

		if (!subscriptionId || subscriptionStatus === 'canceled') {
			console.log('I AM HERE ', subscriptionId, subscriptionStatus);
			notification.error({
				message: `You don't have an active ${type} subscription to cancel. Please subscribe or contact support.`,
			});
			return;
		}

		dispatch(
			openModal({
				modalId: MODAL_IDS.CANCEL_REASON_MODAL,
				data: {
					subscriptionType: type,
				},
			}),
		);
	};

	const handleResellerSendMessage = (reason: string, type: 'Inbound' | 'Outbound') => {
		const subscriptionId =
			type === 'Inbound'
				? resellerData?.inboundSubscriptionId
				: resellerData?.outboundSubscriptionId;
		const subscriptionStatus =
			type === 'Inbound'
				? resellerData?.inboundSubscriptionStatus
				: resellerData?.outboundSubscriptionStatus;

		console.log(subscriptionId, subscriptionStatus);

		if (!subscriptionId || subscriptionStatus === 'canceled') {
			notification.error({
				message: `You don't have an active ${type} subscription to cancel. Please subscribe or contact support.`,
			});
			return;
		}
		cancelResellerSubscriptionMutation.mutate({
			reason,
			subscriptionType: type,
		});
	};

	const isSubscriptionNA = billingOverview?.data?.inboundPlanStatus === 'N/A';
	const isInboundActive =
		billingOverview?.data?.inboundPlanStatus &&
		billingOverview?.data?.inboundPlanStatus !== 'canceled' &&
		!resellerData?.inboundScheduleSubCancelOn;
	const isOutboundActive =
		billingOverview?.data?.outboundPlanStatus &&
		billingOverview?.data?.outboundPlanStatus !== 'canceled' &&
		!resellerData?.outboundScheduleSubCancelOn;

	const modalData = useAppSelector(getModalData) as {
		subscriptionType: 'Inbound' | 'Outbound';
	} | null;

	return (
		<ResellerDashboard
			activeKey={ResellerSiderItem.SUBSCRIPTION}
			isPageLoading={isLoadingBillingOverview}
		>
			<div className="h-full p-6">
				<div ref={resellerSubscriptionTourRef1}>
					<div className="flex items-center justify-between mb-6">
						<div>
							<Title level={2} className="!mb-2">
								Subscription Management
							</Title>
							<Text type="secondary">
								Manage your subscription below. Cancel or update your payment information as needed.
							</Text>
						</div>

						<Button
							type="default"
							size="large"
							className={
								!resellerData?.tutorial_steps?.subscription_tutorial
									? 'bg-[#F1F8FF] hover:bg-[#F1F8FF] text-blue-600 has-ping overflow-hidden'
									: ''
							}
							onClick={handleTutorialClick}
						>
							<BookOpenIcon size={16} />
							<span>Tutorials</span>
						</Button>
					</div>

					<Card title="Subscription Overview" className="m-3">
						<div style={{ display: 'flex', justifyContent: 'space-between' }}>
							<div>
								<Text strong>Base Plan Status(Inbound): </Text>
								<Text
									type={
										billingOverview?.data?.inboundPlanStatus === 'active' ||
										billingOverview?.data?.inboundPlanStatus === 'trialing'
											? 'success'
											: 'danger'
									}
								>
									{billingOverview?.data?.inboundPlanStatus}
								</Text>
							</div>
							<div>
								<Text strong>Outbound Plan Status: </Text>
								<Text
									type={
										billingOverview?.data?.outboundPlanStatus === 'active' ? 'success' : 'danger'
									}
								>
									{billingOverview?.data?.outboundPlanStatus || 'NA'}
								</Text>
							</div>
						</div>
					</Card>
				</div>

				<div className="my-4">
					<RenderCouponInfo
						appliedCoupons={couponData?.data?.appliedCoupons as any}
						onUpdate={refreshCoupons}
						removeDiscount={removeDiscount}
						resellerView={true}
					/>
				</div>

				<div>
					<CouponRedemption
						updateSuccess={refreshCoupons}
						applyCouponCode={applyCouponCode}
						resellerView={true}
					/>
				</div>

				<Card ref={resellerSubscriptionTourRef2} title="Actions" style={{ marginTop: 24 }}>
					{isSubscriptionNA ? (
						<Button
							icon={<ShoppingCartOutlined />}
							onClick={() => setPurchaseSeatsModal(true)}
							type="primary"
						>
							Purchase Subscription
						</Button>
					) : (
						<>
							<Button
								icon={<DollarOutlined />}
								onClick={handleUpdatePaymentMethodConfirm}
								loading={stripeUpdatePaymentMethodMutation.isPending}
								type="primary"
								style={{ marginRight: '8px' }}
							>
								Update Payment Method
							</Button>
							<Button
								danger
								onClick={() => handleCancelSubscription('Inbound')}
								disabled={!isInboundActive}
								loading={cancelResellerSubscriptionMutation.isPending}
								style={{ marginRight: '8px' }}
							>
								Cancel Inbound Subscription
							</Button>
							<Button
								danger
								onClick={() => handleCancelSubscription('Outbound')}
								disabled={!isOutboundActive}
								loading={cancelResellerSubscriptionMutation.isPending}
							>
								Cancel Outbound Subscription
							</Button>
						</>
					)}
				</Card>

				<CancelSubReasonModal
					reseller={true}
					handleResellerSubscriptionCancel={(reason: string) => {
						if (!modalData?.subscriptionType) {
							message.error('Subscription type not found');
							return;
						}
						handleResellerSendMessage(reason, modalData.subscriptionType);
					}}
					loading={cancelResellerSubscriptionMutation.isPending}
				/>

				{!isSubscriptionNA && (
					<Card title="Sub Accounts" style={{ marginTop: 24 }}>
						{[]} {/* Empty array as requested */}
					</Card>
				)}

				<Modal
					title="Purchase Subscription"
					open={purchaseSeatsModal}
					onOk={handlePurchaseSeats}
					onCancel={() => setPurchaseSeatsModal(false)}
					okText="Purchase"
				>
					<Title level={4}>Buy seats for your reseller dashboard</Title>
					<Text>How many seats would you like to purchase?</Text>
					<div style={{ marginTop: 16 }}>
						<InputNumber
							min={5}
							value={seatsToBuy}
							onChange={(value) => setSeatsToBuy(value)}
							style={{ width: '100%' }}
						/>
					</div>
				</Modal>
			</div>
			<TutorialsModal
				isOpen={isTutorialsModalOpen}
				onClose={() => setIsTutorialsModalOpen(false)}
				tutorialItems={tutorialItems}
			>
				<TourButton onClick={handleTourClick} page="subscription" />
			</TutorialsModal>
			<ResellerTour tourSteps={subscriptionTourSteps} />
		</ResellerDashboard>
	);
};

export default ResellerSubscription;
